import { useAuth0 } from "@auth0/auth0-react";
import axios, { type AxiosResponse } from "axios";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import isEqual from "lodash/isEqual";

import { useAppContext } from "../AppProviders";
import { useCompaniesFetchAll } from "../api/CompaniesApi";
import type { CompaniesApi } from "../api/Companies";
import { API_URL, isMocksEnabled } from "../api/helpers";
import type { RequestApi } from "../api/index";
import {
  WORKFLOW_REPORT_STATUS_ENUM,
  type ReportSummaryApi,
  type ReportSummaryItemApi,
  type ReportsPendingReviewItem,
  type ReportsPendingReviewResponse,
} from "../api/Reports";
import { useFamaFlags } from "../api/FamaFlags";
import { usePermissionValidator } from "../api/UserPermissionValidators";
import { useGetWorkflowsCQA, useGetWorkflowsIQA } from "../api/WorkflowData";
import type { WorkflowRunEx, WorkflowRunExPage } from "../api/Workflows";
import type { ListCompareResult } from "../360/ThreeSixtyCompare";
import type { MisconductListTabKey } from "./list-views-v2026/misconductListMappers";
import { getReportsInfoListMock } from "../__mocks__/reportsList";
import { CompareUiListSyncButton } from "./CompareUiListSyncButton";
import { MISCONDUCT_COMPARE_UI_LIST_BY_ROW_ID } from "./compareUiListBackfill";
import {
  CompareCountsCell,
  type CompareProgress,
  type OnComparePageProgress,
} from "./compareProgress";

const COMPARE_PAGE_SIZE = 100;
/** Legacy ``GET /reports/summary/in_process`` only supports 25 rows per page. */
const LEGACY_IN_PROCESS_COMPARE_PAGE_SIZE = 25;
/** Max pages per side when walking legacy / lists APIs (same idea as 360 finished cap). */
const MAX_COMPARE_PAGES = 80;

type CompareCompanyFilter = {
  companyUuid: string;
  companyName: string;
};

type CompareGetParams = Record<string, string | number | undefined>;

type CompareGetFn = (
  url: string,
  params: CompareGetParams,
  companiesHeader?: string
) => Promise<AxiosResponse<{ timestamp?: number } & Record<string, unknown>>>;

function companyIdFromApi(item: CompaniesApi): string {
  return item.external_id ?? item.name;
}

async function compareApiGet(
  accessToken: string,
  url: string,
  params: CompareGetParams,
  companiesHeader?: string
): Promise<AxiosResponse> {
  const headers: Record<string, string> = { Authorization: accessToken };
  if (companiesHeader) {
    headers.companies_header = companiesHeader;
  }
  const search = new URLSearchParams();
  for (const [key, val] of Object.entries(params)) {
    if (val === undefined) continue;
    search.set(key, String(val));
  }
  const qs = search.toString();
  return axios.get(qs ? `${url}?${qs}` : url, { headers });
}

type PagedCompareRows = {
  rows: ComparableReportRow[];
  truncated: boolean;
  reportedTotal: number | null;
};

/** Narrow fields shared enough between legacy and lists rows to diff payloads meaningfully. */
type ComparableReportRow = {
  report_uuid: string;
  company_uuid: string;
};

function errMsg(e: unknown): string {
  if (e instanceof Error) return e.message;
  return String(e);
}

function comparableFromReportSummary(
  r: ReportSummaryItemApi
): ComparableReportRow | null {
  if (!r.report_uuid) return null;
  return {
    report_uuid: r.report_uuid,
    company_uuid: r.company_uuid ?? "",
  };
}

function comparableFromPendingItem(
  r: ReportsPendingReviewItem
): ComparableReportRow | null {
  if (!r.report_uuid) return null;
  return {
    report_uuid: r.report_uuid,
    company_uuid: r.company_uuid ?? "",
  };
}

function comparableFromWorkflowRun(
  w: WorkflowRunEx
): ComparableReportRow | null {
  const p = w.payload;
  if (!p?.report_uuid) return null;
  return {
    report_uuid: String(p.report_uuid),
    company_uuid: String(p.company_uuid ?? ""),
  };
}

function analyzeReportRowPair(
  id: string,
  title: string,
  v2025Label: string,
  v2026Label: string,
  v2025Endpoint: string,
  v2026Endpoint: string,
  rowsV2025: ComparableReportRow[] | null,
  rows2026: ComparableReportRow[] | null,
  tV2025: number | null,
  t2026: number | null,
  errV2025: string | null,
  err2026: string | null,
  note?: string,
  compareTruncated?: boolean
): ListCompareResult {
  const a = rowsV2025 ?? [];
  const b = rows2026 ?? [];
  const setA = new Set(a.map((r) => r.report_uuid));
  const setB = new Set(b.map((r) => r.report_uuid));
  const onlyInV2025 = [...setA].filter((u) => !setB.has(u)).sort();
  const onlyInV2026 = [...setB].filter((u) => !setA.has(u)).sort();
  const common = [...setA].filter((u) => setB.has(u));
  const mapA = new Map(a.map((r) => [r.report_uuid, r]));
  const mapB = new Map(b.map((r) => [r.report_uuid, r]));
  let mismatchedInCommon = 0;
  for (const u of common) {
    if (!isEqual(mapA.get(u), mapB.get(u))) {
      mismatchedInCommon += 1;
    }
  }
  return {
    id,
    title,
    v2025Label,
    v2026Label,
    v2025Endpoint,
    v2026Endpoint,
    v2025Count: errV2025 ? null : a.length,
    v2026Count: err2026 ? null : b.length,
    onlyInV2025,
    onlyInV2026,
    commonUuids: common.length,
    mismatchedInCommon,
    v2025Ms: tV2025,
    v2026Ms: t2026,
    v2025Error: errV2025,
    v2026Error: err2026,
    note,
    compareTruncated,
  };
}

function rowsFromReportSummaryApi(api: ReportSummaryApi): ComparableReportRow[] {
  return (api.report_summaries ?? [])
    .map(comparableFromReportSummary)
    .filter((x): x is ComparableReportRow => x != null);
}

function rowsFromPendingApi(res: ReportsPendingReviewResponse): ComparableReportRow[] {
  return (res.items ?? [])
    .map(comparableFromPendingItem)
    .filter((x): x is ComparableReportRow => x != null);
}

/** Stop when the API returns an empty page or all reported rows are loaded. */
function shouldStopComparePagination(
  rawPageRowCount: number,
  loadedCount: number,
  reportedTotal: number | null
): boolean {
  if (rawPageRowCount === 0) return true;
  if (reportedTotal != null && loadedCount >= reportedTotal) return true;
  return false;
}

async function paginateLegacyReportSummaries(
  fetchPage: (page: number) => Promise<ReportSummaryApi>,
  onPageLoaded?: OnComparePageProgress
): Promise<PagedCompareRows> {
  const out: ComparableReportRow[] = [];
  let reportedTotal: number | null = null;
  let truncated = false;
  for (let page = 0; page < MAX_COMPARE_PAGES; page++) {
    const api = await fetchPage(page);
    if (reportedTotal == null) reportedTotal = api.total_rows;
    const rawPageRowCount = api.report_summaries?.length ?? 0;
    const chunk = rowsFromReportSummaryApi(api);
    out.push(...chunk);
    onPageLoaded?.(out.length, reportedTotal);
    if (shouldStopComparePagination(rawPageRowCount, out.length, reportedTotal)) {
      break;
    }
    if (page === MAX_COMPARE_PAGES - 1 && reportedTotal != null && out.length < reportedTotal) {
      truncated = true;
    }
  }
  if (reportedTotal != null && out.length < reportedTotal) {
    truncated = true;
  }
  return { rows: out, truncated, reportedTotal };
}

async function paginateLegacyPendingOrReviewed(
  fetchPage: (page: number) => Promise<ReportsPendingReviewResponse>,
  onPageLoaded?: OnComparePageProgress
): Promise<PagedCompareRows> {
  const out: ComparableReportRow[] = [];
  let reportedTotal: number | null = null;
  let truncated = false;
  for (let page = 1; page <= MAX_COMPARE_PAGES; page++) {
    const res = await fetchPage(page);
    if (reportedTotal == null) reportedTotal = res.total_items;
    const rawPageRowCount = res.items?.length ?? 0;
    const chunk = rowsFromPendingApi(res);
    out.push(...chunk);
    onPageLoaded?.(out.length, reportedTotal);
    if (shouldStopComparePagination(rawPageRowCount, out.length, reportedTotal)) {
      break;
    }
    if (page === MAX_COMPARE_PAGES && reportedTotal != null && out.length < reportedTotal) {
      truncated = true;
    }
  }
  if (reportedTotal != null && out.length < reportedTotal) {
    truncated = true;
  }
  return { rows: out, truncated, reportedTotal };
}

async function paginateMisconductListSummaries(
  compareGet: CompareGetFn,
  tab: MisconductListTabKey,
  companiesHeader?: string,
  errorsOnly?: boolean,
  onPageLoaded?: OnComparePageProgress
): Promise<PagedCompareRows> {
  if (isMocksEnabled()) {
    const api = await getReportsInfoListMock();
    const rows = rowsFromReportSummaryApi(api);
    onPageLoaded?.(rows.length, api.total_rows);
    return { rows, truncated: false, reportedTotal: api.total_rows };
  }
  const out: ComparableReportRow[] = [];
  let reportedTotal: number | null = null;
  let truncated = false;
  for (let page = 1; page <= MAX_COMPARE_PAGES; page++) {
    const offset = (page - 1) * COMPARE_PAGE_SIZE;
    const params: CompareGetParams = {
      offset,
      size: COMPARE_PAGE_SIZE,
    };
    if (tab === "in_process" && errorsOnly) {
      params.errors_only = 1;
    }
    const response = await compareGet(
      `${API_URL}${listsPath(tab)}`,
      params,
      companiesHeader
    );
    const api = response.data as unknown as ReportSummaryApi;
    api.timestamp = Date.now();
    if (reportedTotal == null) reportedTotal = api.total_rows;
    const rawPageRowCount = api.report_summaries?.length ?? 0;
    const chunk = rowsFromReportSummaryApi(api);
    out.push(...chunk);
    onPageLoaded?.(out.length, reportedTotal);
    if (shouldStopComparePagination(rawPageRowCount, out.length, reportedTotal)) {
      break;
    }
    if (page === MAX_COMPARE_PAGES && reportedTotal != null && out.length < reportedTotal) {
      truncated = true;
    }
  }
  if (reportedTotal != null && out.length < reportedTotal) {
    truncated = true;
  }
  return { rows: out, truncated, reportedTotal };
}

async function paginateWorkflowRuns(
  fetchPage: (page: number) => Promise<WorkflowRunExPage>,
  filterCompanyUuid?: string,
  onPageLoaded?: OnComparePageProgress
): Promise<PagedCompareRows> {
  const out: ComparableReportRow[] = [];
  let reportedTotal: number | null = null;
  let truncated = false;
  /** IQA/CQA API totals are unfiltered; do not compare filtered out.length to reportedTotal. */
  const clientSideCompanyFilter = Boolean(filterCompanyUuid);
  for (let page = 0; page < MAX_COMPARE_PAGES; page++) {
    const [runs, total] = await fetchPage(page);
    if (reportedTotal == null) reportedTotal = total;
    const rawPageRowCount = runs?.length ?? 0;
    let chunk = (runs ?? [])
      .map(comparableFromWorkflowRun)
      .filter((x): x is ComparableReportRow => x != null);
    if (filterCompanyUuid) {
      chunk = chunk.filter((r) => r.company_uuid === filterCompanyUuid);
    }
    out.push(...chunk);
    onPageLoaded?.(out.length, clientSideCompanyFilter ? null : reportedTotal);

    if (clientSideCompanyFilter) {
      if (rawPageRowCount === 0) break;
      if (page === MAX_COMPARE_PAGES - 1 && rawPageRowCount >= COMPARE_PAGE_SIZE) {
        truncated = true;
      }
    } else if (
      shouldStopComparePagination(rawPageRowCount, out.length, reportedTotal)
    ) {
      break;
    } else if (
      page === MAX_COMPARE_PAGES - 1 &&
      reportedTotal != null &&
      out.length < reportedTotal
    ) {
      truncated = true;
    }
  }
  if (
    !clientSideCompanyFilter &&
    reportedTotal != null &&
    out.length < reportedTotal
  ) {
    truncated = true;
  }
  return { rows: out, truncated, reportedTotal };
}

function buildCompareNote(
  v2025: PagedCompareRows,
  v2026: PagedCompareRows,
  extra?: string
): { note: string; compareTruncated: boolean } {
  const compareTruncated = v2025.truncated || v2026.truncated;
  const parts: string[] = [];
  if (compareTruncated) {
    parts.push(
      `Compare capped at ${MAX_COMPARE_PAGES} pages (size ${COMPARE_PAGE_SIZE}). Legacy loaded ${v2025.rows.length}` +
        (v2025.reportedTotal != null ? ` / total_rows ${v2025.reportedTotal}` : "") +
        `; lists loaded ${v2026.rows.length}` +
        (v2026.reportedTotal != null ? ` / total_rows ${v2026.reportedTotal}` : "") +
        `. Sync disabled.`
    );
  } else {
    parts.push(
      `All pages loaded (size ${COMPARE_PAGE_SIZE}, empty search). Legacy ${v2025.rows.length}` +
        (v2025.reportedTotal != null ? ` / total_rows ${v2025.reportedTotal}` : "") +
        `; lists ${v2026.rows.length}` +
        (v2026.reportedTotal != null ? ` / total_rows ${v2026.reportedTotal}` : "") +
        `.`
    );
  }
  if (extra) parts.push(extra);
  return { note: parts.join(" "), compareTruncated };
}

async function fetchLegacyInProgressSummaryPage(
  requestApi: RequestApi,
  compareGet: CompareGetFn,
  enableProductReportsForClientTab: boolean | undefined,
  page: number,
  companyFilter: CompareCompanyFilter | null
): Promise<ReportSummaryApi> {
  if (isMocksEnabled()) {
    throw new Error("Mocks enabled — disable mocks to compare live APIs.");
  }
  const input: Record<string, string | number | string[] | boolean | undefined> =
    {
      person_name: "",
      page_size: COMPARE_PAGE_SIZE,
      page,
      workflow_status: [WORKFLOW_REPORT_STATUS_ENUM.IN_PROGRESS],
      include_three_sixty: true,
    };
  if (companyFilter?.companyName) {
    input.company_name = companyFilter.companyName;
  }
  const endpoint = enableProductReportsForClientTab
    ? "/product_reports/misconduct/in_progress/full"
    : "/reports/summary/in_progress";
  const url = API_URL + endpoint;
  const response =
    enableProductReportsForClientTab && companyFilter?.companyUuid
      ? await compareGet(
          url,
          input as CompareGetParams,
          companyFilter.companyUuid
        )
      : await requestApi.get(url, input);
  response.data.timestamp = Date.now();
  return response.data as ReportSummaryApi;
}

async function fetchLegacyReportsInProcessQueuePage(
  requestApi: RequestApi,
  page: number,
  companyFilter: CompareCompanyFilter | null,
  pageSize: number = LEGACY_IN_PROCESS_COMPARE_PAGE_SIZE
): Promise<ReportSummaryApi> {
  if (isMocksEnabled()) {
    throw new Error("Mocks enabled — disable mocks to compare live APIs.");
  }
  const input: Record<string, string | number | undefined> = {
    person_name: "",
    page_size: pageSize,
    page,
  };
  if (companyFilter?.companyName) {
    input.company_name = companyFilter.companyName;
  }
  const response = await requestApi.get(
    `${API_URL}/reports/summary/in_process`,
    input
  );
  response.data.timestamp = Date.now();
  return response.data as ReportSummaryApi;
}

async function fetchLegacyPendingOrReviewedPage(
  requestApi: RequestApi,
  base: "workflow/in_review" | "workflow/reviewed",
  companyFilter: CompareCompanyFilter | null,
  page: number
): Promise<ReportsPendingReviewResponse> {
  if (isMocksEnabled()) {
    throw new Error("Mocks enabled — disable mocks to compare live APIs.");
  }
  const company_uuids = companyFilter ? [companyFilter.companyUuid] : [];
  const response = await requestApi.get(`${API_URL}/${base}`, {
    individual: false,
    search: "",
    page_size: COMPARE_PAGE_SIZE,
    page,
    company_uuids,
  });
  response.data.timestamp = Date.now();
  return response.data as ReportsPendingReviewResponse;
}

type CompareTabRowState = {
  id: string;
  title: string;
  v2025Endpoint: string;
  v2026Endpoint: string;
  result: ListCompareResult | null;
  progress: CompareProgress | null;
  running: boolean;
  onCompare: () => void;
};

function MisconductCompareTable({
  tabs,
  canSync,
  compareAllRunning,
  onCompareAll,
  anyLoaded,
}: {
  tabs: CompareTabRowState[];
  canSync: boolean;
  compareAllRunning: boolean;
  onCompareAll: () => void;
  anyLoaded: boolean;
}) {
  return (
    <div className="overflow-x-auto border border-gray-400 text-sm">
      <div className="flex flex-wrap items-center gap-2 p-2 bg-gray-100 border-b border-gray-400">
        <button
          type="button"
          className="px-2 py-0.5 bg-gray-700 text-white text-xs rounded disabled:opacity-50"
          disabled={
            compareAllRunning || tabs.some((t) => t.running) || isMocksEnabled()
          }
          onClick={onCompareAll}
        >
          {compareAllRunning ? "Comparing all…" : "Compare all tabs (sequential)"}
        </button>
        {anyLoaded ? (
          <span className="text-xs text-gray-600">
            Each tab loads on its own — use per-row Compare to refresh one list.
          </span>
        ) : null}
      </div>
      <table className="min-w-full border-collapse">
        <thead>
          <tr className="bg-gray-200 text-left">
            <th className="border border-gray-400 p-2">List</th>
            <th className="border border-gray-400 p-2">v2025 source</th>
            <th className="border border-gray-400 p-2">v2026 source</th>
            <th className="border border-gray-400 p-2">Counts</th>
            <th className="border border-gray-400 p-2">report_uuid diff</th>
            <th className="border border-gray-400 p-2">
              Comparable row (common)
            </th>
            <th className="border border-gray-400 p-2">ms</th>
            <th className="border border-gray-400 p-2">Errors</th>
            {canSync ? (
              <th className="border border-gray-400 p-2">Sync ui_list</th>
            ) : null}
          </tr>
        </thead>
        <tbody>
          {tabs.map((tab) => {
            const r = tab.result;
            const loaded = r != null;
            return (
              <tr key={tab.id} className="align-top">
                <td className="border border-gray-400 p-2 font-semibold">
                  {tab.title}
                  {loaded && r.note ? (
                    <div className="text-xs font-normal text-gray-600 mt-1">
                      {r.note}
                    </div>
                  ) : null}
                  <div className="mt-2">
                    <button
                      type="button"
                      className="px-2 py-0.5 bg-gray-800 text-white text-xs rounded disabled:opacity-50"
                      disabled={
                        tab.running ||
                        compareAllRunning ||
                        isMocksEnabled()
                      }
                      onClick={tab.onCompare}
                    >
                      {tab.running
                        ? "Comparing…"
                        : loaded
                        ? "Re-compare"
                        : "Compare"}
                    </button>
                  </div>
                </td>
                <td className="border border-gray-400 p-2 font-mono text-xs">
                  {tab.v2025Endpoint}
                  {loaded ? (
                    <div className="text-gray-500">{r.v2025Label}</div>
                  ) : null}
                </td>
                <td className="border border-gray-400 p-2 font-mono text-xs">
                  {tab.v2026Endpoint}
                  {loaded ? (
                    <div className="text-gray-500">{r.v2026Label}</div>
                  ) : null}
                </td>
                <td className="border border-gray-400 p-2">
                  <CompareCountsCell
                    progress={tab.progress}
                    result={r}
                    running={tab.running}
                  />
                </td>
                <td className="border border-gray-400 p-2 text-xs max-w-md">
                  {!loaded ? (
                    <span className="text-gray-400">—</span>
                  ) : (
                    <>
                      <div>only v2025 ({r.onlyInV2025.length})</div>
                      <pre className="whitespace-pre-wrap break-all max-h-32 overflow-auto bg-gray-50 p-1">
                        {r.onlyInV2025.slice(0, 40).join("\n")}
                        {r.onlyInV2025.length > 40
                          ? `\n… +${r.onlyInV2025.length - 40} more`
                          : ""}
                      </pre>
                      <div className="mt-2">
                        only v2026 ({r.onlyInV2026.length})
                      </div>
                      <pre className="whitespace-pre-wrap break-all max-h-32 overflow-auto bg-gray-50 p-1">
                        {r.onlyInV2026.slice(0, 40).join("\n")}
                        {r.onlyInV2026.length > 40
                          ? `\n… +${r.onlyInV2026.length - 40} more`
                          : ""}
                      </pre>
                    </>
                  )}
                </td>
                <td className="border border-gray-400 p-2 text-xs">
                  {!loaded ? (
                    <span className="text-gray-400">—</span>
                  ) : (
                    <>
                      <div>common report_uuid: {r.commonUuids}</div>
                      <div
                        className={
                          r.mismatchedInCommon === 0
                            ? "text-green-700"
                            : "text-amber-700"
                        }
                      >
                        unequal comparable fields: {r.mismatchedInCommon}
                      </div>
                    </>
                  )}
                </td>
                <td className="border border-gray-400 p-2 font-mono text-xs whitespace-nowrap">
                  {!loaded ? (
                    <span className="text-gray-400">—</span>
                  ) : (
                    <>
                      <div>
                        v2025: {r.v2025Ms != null ? `${r.v2025Ms}ms` : "—"}
                      </div>
                      <div>
                        v2026: {r.v2026Ms != null ? `${r.v2026Ms}ms` : "—"}
                      </div>
                    </>
                  )}
                </td>
                <td className="border border-gray-400 p-2 text-xs text-red-700 max-w-xs">
                  {!loaded ? (
                    <span className="text-gray-400">—</span>
                  ) : (
                    <>
                      {r.v2025Error ? <div>v2025: {r.v2025Error}</div> : null}
                      {r.v2026Error ? <div>v2026: {r.v2026Error}</div> : null}
                      {!r.v2025Error && !r.v2026Error ? "—" : null}
                    </>
                  )}
                </td>
                {canSync ? (
                  <td className="border border-gray-400 p-2">
                    {!loaded ? (
                      <span className="text-gray-400 text-xs">—</span>
                    ) : (
                      <>
                        <CompareUiListSyncButton
                          rowTitle={r.title}
                          onlyInLegacy={r.onlyInV2025}
                          uiList={
                            MISCONDUCT_COMPARE_UI_LIST_BY_ROW_ID[r.id] ?? "RUN"
                          }
                          reportType="MISCONDUCT"
                          disabled={
                            Boolean(r.v2025Error) ||
                            Boolean(r.compareTruncated)
                          }
                        />
                        {r.compareTruncated ? (
                          <span className="text-amber-700 text-xs mt-1">
                            Sync disabled — compare did not load all pages.
                          </span>
                        ) : null}
                      </>
                    )}
                  </td>
                ) : null}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function listsPath(tab: MisconductListTabKey): string {
  return `/lists/misconduct/${tab}`;
}

type MisconductCompareTabConfig = {
  id: string;
  title: string;
  v2025Endpoint: string;
  v2026Endpoint: string;
  fetchV2025: (onPageLoaded?: OnComparePageProgress) => Promise<PagedCompareRows>;
  fetch2026: (onPageLoaded?: OnComparePageProgress) => Promise<PagedCompareRows>;
};

async function runReportComparePair(
  config: MisconductCompareTabConfig,
  extraNote?: string,
  onProgress?: (progress: CompareProgress) => void
): Promise<ListCompareResult> {
  let rowsV2025: ComparableReportRow[] | null = null;
  let rows2026: ComparableReportRow[] | null = null;
  let tV2025: number | null = null;
  let t2026: number | null = null;
  let eV2025: string | null = null;
  let e2026: string | null = null;
  let note = extraNote ?? "";
  let compareTruncated = false;
  const t0 = performance.now();
  let pagedV2025: PagedCompareRows | null = null;
  let v2025Loaded = 0;
  let v2025ReportedTotal: number | null = null;
  onProgress?.({
    phase: "v2025",
    v2025Loaded: 0,
    v2026Loaded: 0,
    v2025ReportedTotal: null,
    v2026ReportedTotal: null,
  });
  try {
    pagedV2025 = await config.fetchV2025((loaded, reportedTotal) => {
      v2025Loaded = loaded;
      if (reportedTotal != null) v2025ReportedTotal = reportedTotal;
      onProgress?.({
        phase: "v2025",
        v2025Loaded: loaded,
        v2026Loaded: 0,
        v2025ReportedTotal: reportedTotal ?? v2025ReportedTotal,
        v2026ReportedTotal: null,
      });
    });
    rowsV2025 = pagedV2025.rows;
    v2025Loaded = pagedV2025.rows.length;
    v2025ReportedTotal = pagedV2025.reportedTotal;
    tV2025 = Math.round(performance.now() - t0);
  } catch (e) {
    eV2025 = errMsg(e);
  }
  const t1 = performance.now();
  let paged2026: PagedCompareRows | null = null;
  onProgress?.({
    phase: "v2026",
    v2025Loaded,
    v2026Loaded: 0,
    v2025ReportedTotal,
    v2026ReportedTotal: null,
  });
  try {
    paged2026 = await config.fetch2026((loaded, reportedTotal) => {
      onProgress?.({
        phase: "v2026",
        v2025Loaded,
        v2026Loaded: loaded,
        v2025ReportedTotal,
        v2026ReportedTotal: reportedTotal,
      });
    });
    rows2026 = paged2026.rows;
    t2026 = Math.round(performance.now() - t1);
  } catch (e) {
    e2026 = errMsg(e);
  }
  if (pagedV2025 && paged2026) {
    const built = buildCompareNote(pagedV2025, paged2026, extraNote);
    note = built.note;
    compareTruncated = built.compareTruncated;
  }
  return analyzeReportRowPair(
    config.id,
    config.title,
    "Legacy list / summary / workflow API",
    "GET /lists/misconduct/… (report summaries)",
    config.v2025Endpoint,
    config.v2026Endpoint,
    rowsV2025,
    rows2026,
    tV2025,
    t2026,
    eV2025,
    e2026,
    note,
    compareTruncated
  );
}

/**
 * Dev / QA: compare legacy misconduct list sources with ``GET /lists/misconduct/*`` summaries
 * (same split as {@link MisconductListViewByFlag}).
 */
const MisconductCompare = () => {
  const { requestApi, companiesFromAuth } = useAppContext();
  const { getAccessTokenSilently } = useAuth0();
  const perms = usePermissionValidator();
  const canSync = perms.canResumeWorkflow;
  const { enableProductReportsForClientTab } = useFamaFlags();
  const iqa = useGetWorkflowsIQA();
  const cqa = useGetWorkflowsCQA();
  const {
    mutate: loadCompanies,
    data: companiesData,
    isLoading: companiesLoading,
  } = useCompaniesFetchAll();

  const [selectedCompanyUuid, setSelectedCompanyUuid] = useState("");
  const companies = useMemo(() => companiesData ?? [], [companiesData]);

  const companyFilter = useMemo((): CompareCompanyFilter | null => {
    if (!selectedCompanyUuid) return null;
    const match = companies.find(
      (c) => companyIdFromApi(c) === selectedCompanyUuid
    );
    if (!match) return null;
    return {
      companyUuid: selectedCompanyUuid,
      companyName: match.name,
    };
  }, [selectedCompanyUuid, companies]);

  const compareGet = useCallback<CompareGetFn>(
    async (url, params, companiesHeader) => {
      const token = await getAccessTokenSilently();
      return compareApiGet(token, url, params, companiesHeader);
    },
    [getAccessTokenSilently]
  );

  const companiesHeader = companyFilter?.companyUuid;
  const filterCompanyUuid = companyFilter?.companyUuid;

  useEffect(() => {
    loadCompanies();
  }, [loadCompanies]);

  const compareTabConfigs = useMemo((): MisconductCompareTabConfig[] => {
    const inProgressLegacyEndpoint = enableProductReportsForClientTab
      ? "/product_reports/misconduct/in_progress/full"
      : "/reports/summary/in_progress";

    return [
      {
        id: "in_process_queue",
        title: "In process (queue)",
        v2025Endpoint: "/reports/summary/in_process",
        v2026Endpoint: listsPath("in_process"),
        fetchV2025: (onPageLoaded) =>
          paginateLegacyReportSummaries(
            (page) =>
              fetchLegacyReportsInProcessQueuePage(
                requestApi,
                page,
                companyFilter,
                LEGACY_IN_PROCESS_COMPARE_PAGE_SIZE
              ),
            onPageLoaded
          ),
        fetch2026: (onPageLoaded) =>
          paginateMisconductListSummaries(
            compareGet,
            "in_process",
            companiesHeader,
            undefined,
            onPageLoaded
          ),
      },
      {
        id: "in_progress",
        title: "In progress",
        v2025Endpoint: inProgressLegacyEndpoint,
        v2026Endpoint: listsPath("in_progress"),
        fetchV2025: (onPageLoaded) =>
          paginateLegacyReportSummaries(
            (page) =>
              fetchLegacyInProgressSummaryPage(
                requestApi,
                compareGet,
                enableProductReportsForClientTab,
                page,
                companyFilter
              ),
            onPageLoaded
          ),
        fetch2026: (onPageLoaded) =>
          paginateMisconductListSummaries(
            compareGet,
            "in_progress",
            companiesHeader,
            undefined,
            onPageLoaded
          ),
      },
      {
        id: "pending_review",
        title: "Pending review",
        v2025Endpoint: "/workflow/in_review",
        v2026Endpoint: listsPath("pending_review"),
        fetchV2025: (onPageLoaded) =>
          paginateLegacyPendingOrReviewed(
            (page) =>
              fetchLegacyPendingOrReviewedPage(
                requestApi,
                "workflow/in_review",
                companyFilter,
                page
              ),
            onPageLoaded
          ),
        fetch2026: (onPageLoaded) =>
          paginateMisconductListSummaries(
            compareGet,
            "pending_review",
            companiesHeader,
            undefined,
            onPageLoaded
          ),
      },
      {
        id: "reviewed",
        title: "Reviewed",
        v2025Endpoint: "/workflow/reviewed",
        v2026Endpoint: listsPath("reviewed"),
        fetchV2025: (onPageLoaded) =>
          paginateLegacyPendingOrReviewed(
            (page) =>
              fetchLegacyPendingOrReviewedPage(
                requestApi,
                "workflow/reviewed",
                companyFilter,
                page
              ),
            onPageLoaded
          ),
        fetch2026: (onPageLoaded) =>
          paginateMisconductListSummaries(
            compareGet,
            "reviewed",
            companiesHeader,
            undefined,
            onPageLoaded
          ),
      },
      {
        id: "internal_qa",
        title: "Internal QA",
        v2025Endpoint: "/workflowruns/iqa",
        v2026Endpoint: listsPath("internal_qa"),
        fetchV2025: (onPageLoaded) =>
          paginateWorkflowRuns(
            (page) =>
              iqa.mutateAsync({
                search: "",
                page,
                page_size: COMPARE_PAGE_SIZE,
              }),
            filterCompanyUuid,
            onPageLoaded
          ),
        fetch2026: (onPageLoaded) =>
          paginateMisconductListSummaries(
            compareGet,
            "internal_qa",
            companiesHeader,
            undefined,
            onPageLoaded
          ),
      },
      {
        id: "client_qa",
        title: "Client QA",
        v2025Endpoint: "/workflowruns/cqa",
        v2026Endpoint: listsPath("client_qa"),
        fetchV2025: (onPageLoaded) =>
          paginateWorkflowRuns(
            (page) =>
              cqa.mutateAsync({
                search: "",
                page,
                page_size: COMPARE_PAGE_SIZE,
              }),
            filterCompanyUuid,
            onPageLoaded
          ),
        fetch2026: (onPageLoaded) =>
          paginateMisconductListSummaries(
            compareGet,
            "client_qa",
            companiesHeader,
            undefined,
            onPageLoaded
          ),
      },
    ];
  }, [
    requestApi,
    compareGet,
    companyFilter,
    companiesHeader,
    filterCompanyUuid,
    enableProductReportsForClientTab,
    iqa,
    cqa,
  ]);

  const [resultsByTabId, setResultsByTabId] = useState<
    Record<string, ListCompareResult>
  >({});
  const [progressByTabId, setProgressByTabId] = useState<
    Record<string, CompareProgress>
  >({});
  const [runningTabId, setRunningTabId] = useState<string | null>(null);
  const [compareAllRunning, setCompareAllRunning] = useState(false);

  useEffect(() => {
    setResultsByTabId({});
    setProgressByTabId({});
  }, [selectedCompanyUuid]);

  const companyFilterNote = companyFilter
    ? `Company filter: ${companyFilter.companyName}.`
    : undefined;

  const compareTab = useCallback(
    async (tabId: string) => {
      const config = compareTabConfigs.find((t) => t.id === tabId);
      if (!config) return;
      setRunningTabId(tabId);
      setProgressByTabId((prev) => ({
        ...prev,
        [tabId]: {
          phase: "v2025",
          v2025Loaded: 0,
          v2026Loaded: 0,
          v2025ReportedTotal: null,
          v2026ReportedTotal: null,
        },
      }));
      try {
        const result = await runReportComparePair(
          config,
          companyFilterNote,
          (progress) => {
            setProgressByTabId((prev) => ({ ...prev, [tabId]: progress }));
          }
        );
        setResultsByTabId((prev) => ({ ...prev, [tabId]: result }));
      } finally {
        setProgressByTabId((prev) => {
          const next = { ...prev };
          delete next[tabId];
          return next;
        });
        setRunningTabId((current) => (current === tabId ? null : current));
      }
    },
    [compareTabConfigs, companyFilterNote]
  );

  const compareAllTabs = useCallback(async () => {
    setCompareAllRunning(true);
    try {
      for (const config of compareTabConfigs) {
        setRunningTabId(config.id);
        setProgressByTabId((prev) => ({
          ...prev,
          [config.id]: {
            phase: "v2025",
            v2025Loaded: 0,
            v2026Loaded: 0,
            v2025ReportedTotal: null,
            v2026ReportedTotal: null,
          },
        }));
        try {
          const result = await runReportComparePair(
            config,
            companyFilterNote,
            (progress) => {
              setProgressByTabId((prev) => ({ ...prev, [config.id]: progress }));
            }
          );
          setResultsByTabId((prev) => ({ ...prev, [config.id]: result }));
        } finally {
          setProgressByTabId((prev) => {
            const next = { ...prev };
            delete next[config.id];
            return next;
          });
          setRunningTabId((current) =>
            current === config.id ? null : current
          );
        }
      }
    } finally {
      setCompareAllRunning(false);
      setRunningTabId(null);
    }
  }, [compareTabConfigs, companyFilterNote]);

  const tableTabs: CompareTabRowState[] = useMemo(
    () =>
      compareTabConfigs.map((config) => ({
        id: config.id,
        title: config.title,
        v2025Endpoint: config.v2025Endpoint,
        v2026Endpoint: config.v2026Endpoint,
        result: resultsByTabId[config.id] ?? null,
        progress: progressByTabId[config.id] ?? null,
        running: runningTabId === config.id,
        onCompare: () => void compareTab(config.id),
      })),
    [compareTabConfigs, resultsByTabId, progressByTabId, runningTabId, compareTab]
  );

  const anyLoaded = Object.keys(resultsByTabId).length > 0;

  const canAccessMisconductCompare =
    perms.canViewAllReportsWithinCompany &&
    companiesFromAuth?.allCompanies === true;

  if (!canAccessMisconductCompare) {
    return <Navigate to="/unauthorized" replace />;
  }

  return (
    <div className="p-4 max-w-[1400px] mx-auto text-gray-900">
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <h1 className="text-xl font-bold">Misconduct list API compare</h1>
        <Link
          className="text-blue-600 underline text-sm"
          to="/reports/in_progress"
        >
          ← Back to reports
        </Link>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <label htmlFor="misconduct-compare-company" className="text-sm font-medium">
          Company
        </label>
        <select
          id="misconduct-compare-company"
          className="border border-gray-400 rounded px-2 py-1 text-sm min-w-[220px] max-w-full"
          value={selectedCompanyUuid}
          disabled={companiesLoading}
          onChange={(e) => setSelectedCompanyUuid(e.target.value)}
        >
          <option value="">All companies</option>
          {companies.map((c) => {
            const id = companyIdFromApi(c);
            return (
              <option key={id} value={id}>
                {c.name}
              </option>
            );
          })}
        </select>
        {companyFilter ? (
          <span className="text-xs text-gray-600">
            Comparisons use this company on legacy, lists, and workflow requests.
            Internal/client QA legacy rows are filtered by{" "}
            <code className="bg-gray-100 px-1">company_uuid</code> after fetch.
          </span>
        ) : (
          <span className="text-xs text-gray-600">
            All companies — same scope as your account header.
          </span>
        )}
      </div>

      <p className="text-sm text-gray-600 mb-4">
        Compare one tab at a time (legacy loaders vs{" "}
        <code className="bg-gray-100 px-1">GET /lists/misconduct/…</code>
        ). Each row paginates both sides independently so memory stays bounded.
        “Compare all tabs (sequential)” runs one tab after another when you need
        every list. Legacy:{" "}
        <code className="bg-gray-100 px-1">workflow/in_review</code>,{" "}
        <code className="bg-gray-100 px-1">workflow/reviewed</code>,{" "}
        <code className="bg-gray-100 px-1">workflowruns/iqa|cqa</code>. Requires{" "}
        <code className="bg-gray-100 px-1">
          report:view_all_reports_within_company
        </code>{" "}
        and access to all companies (not company-assigned). Green counts when
        loaded row counts match (all pages, up to{" "}
        {MAX_COMPARE_PAGES}×{COMPARE_PAGE_SIZE} per side);{" "}
        <code className="bg-gray-100 px-1">report_uuid</code> columns show
        symmetric difference. “Unequal comparable fields” counts shared{" "}
        <code className="bg-gray-100 px-1">report_uuid</code> rows where{" "}
        <code className="bg-gray-100 px-1">company_uuid</code> differs.
      </p>

      {isMocksEnabled() ? (
        <p className="text-amber-800 text-sm mb-2">
          Mocks are enabled — comparison will fail until mocks are turned off.
        </p>
      ) : null}

      {!perms.canManageReports ? (
        <p className="text-amber-800 text-sm mb-2">
          You may lack report page permissions; some requests will error.
        </p>
      ) : null}

      {!canSync ? (
        <p className="text-amber-800 text-sm mb-2">
          <code className="bg-gray-100 px-1">report:resume_workflow</code> is
          required only for Sync ui_list — comparison below is read-only.
        </p>
      ) : null}

      <MisconductCompareTable
        tabs={tableTabs}
        canSync={canSync}
        compareAllRunning={compareAllRunning}
        onCompareAll={() => void compareAllTabs()}
        anyLoaded={anyLoaded}
      />
    </div>
  );
};

export default MisconductCompare;
